import { Box } from '@hub/box'
import { HorizontalOverflowLargeIcon, TickMediumIcon } from '@hub/icon'
import { LinkBox, LinkOverlay } from '@hub/link'
import { Stack } from '@hub/stack'
import { Text } from '@hub/text'
import {
  CloudinaryImage,
  IMAGE_SIZES_DECK_REEL,
} from '@scentregroup/shared/hub-components/cloudinary-image'
import LinkReel from '@scentregroup/shared/hub-components/link-reel'
import React from 'react'
import { PopularCategory } from './types'
import { useStoresRoute } from './use-stores-route'

const PopularCategoryImage: React.FC<{ category: PopularCategory }> = ({
  category,
}) => {
  if (category.imageUrl) {
    return (
      <CloudinaryImage
        ratio={1}
        resizeMode="crop-center"
        imageSetOrImage={{
          url: category.imageUrl,
          type: 'SQUARE',
        }}
        sizes={IMAGE_SIZES_DECK_REEL}
        loadingMode="eager"
      />
    )
  } else {
    return (
      <Stack
        sx={{
          width: ['size-md', null, 'size-lg'],
          aspectRatio: 1,
          backgroundColor: 'surfaceBrandPrimary',
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: 'surfaceBrandSecondary',
        }}
        shouldWrapChildren={false}
        justify="center"
        align="center"
        textAlign="center"
      >
        <Text as="span">{category.title}</Text>
      </Stack>
    )
  }
}

const PopularCategoryMoreCategoriesImage: React.FC = () => {
  return (
    <Stack
      sx={{
        width: ['size-md', null, 'size-lg'],
        aspectRatio: 1,
        backgroundColor: 'surfaceBrandSecondary',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'surfaceBrandSecondary',
        color: 'iconPrimary',
      }}
      shouldWrapChildren={false}
      justify="center"
      align="center"
    >
      <HorizontalOverflowLargeIcon boxSize="size-icon-lg" />
    </Stack>
  )
}

const SelectedOverlay: React.FC<
  React.PropsWithChildren<{
    isSelected: boolean
  }>
> = ({ isSelected, children }) => {
  if (!isSelected) {
    return <>{children}</>
  }
  return (
    <Box>
      <Box sx={{ position: 'relative' }}>{children}</Box>
      <Stack
        shouldWrapChildren={false}
        justify="center"
        align="center"
        sx={{
          position: 'absolute',
          width: ['size-md', null, 'size-lg'],
          aspectRatio: 1,
          top: 0,
          left: 0,
          right: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          color: 'iconSurfacePrimary',
        }}
      >
        <TickMediumIcon boxSize="size-icon-lg" />
      </Stack>
    </Box>
  )
}

export const PopularCategoriesReel: React.FC<{
  categories: PopularCategory[]
  selectedCategoryId: string | null
  onClickMoreCategories?: () => void
}> = ({ categories, selectedCategoryId, onClickMoreCategories }) => {
  const linkToCategory = useStoresRoute()
  const items = categories
    .map<{
      key: string
      image: React.ReactNode
      label: string
      link?: string
      onClick?: () => void
    }>(category => ({
      key: category.id,
      image: (
        <SelectedOverlay isSelected={category.id === selectedCategoryId}>
          <PopularCategoryImage category={category} />
        </SelectedOverlay>
      ),
      label: category.title || '',
      link: linkToCategory(
        category.id === selectedCategoryId || !category.slug
          ? []
          : [category.slug]
      ),
    }))
    .concat([
      {
        key: 'more',
        image: <PopularCategoryMoreCategoriesImage />,
        label: 'See more categories',
        onClick: onClickMoreCategories,
      },
    ])

  return (
    <LinkReel
      gap="spacing-md"
      sx={{ marginBottom: 'spacing-md', marginTop: 'spacing-md' }}
    >
      {items.map(item => {
        const MaybeLinkBox = item.link ? LinkBox : Box
        return (
          <MaybeLinkBox
            key={item.key}
            sx={{
              flexGrow: 0,
              cursor: 'pointer',
              ['*:hover']: {
                textDecoration: 'underline',
              },
            }}
            onClick={item.onClick}
          >
            <Stack
              sx={{
                width: ['size-md', null, 'size-lg'],
              }}
              gap="spacing-sm"
            >
              {item.image}
              {item.link ? (
                <LinkOverlay href={item.link}>
                  <Text as="span" tone="strong" fontSize="font-lg">
                    {item.label}
                  </Text>
                </LinkOverlay>
              ) : (
                <Text tone="strong" fontSize="font-lg">
                  {item.label}
                </Text>
              )}
            </Stack>
          </MaybeLinkBox>
        )
      })}
    </LinkReel>
  )
}
